'use client';

import { useEffect } from 'react';
import { useChatStore } from '../../stores/chatStore';
import { ChatOverlayProps } from '../../types/chat';
import { ChatContactPanel } from './ChatContactPanel';
import { ChatRoom } from './ChatRoom';

export function ChatOverlay({ isOpen, onClose }: ChatOverlayProps) {
  const {
    activeContactId,
    contacts,
    messages,
    activeProductData,
    openChat,
    closeChat,
    sendMessage,
  } = useChatStore();

  const activeContact = contacts.find(contact => contact.id === activeContactId);
  const activeMessages = activeContactId ? messages[activeContactId] || [] : [];

  // Handle ESC key to close overlay
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeContactId) {
          closeChat();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, activeContactId, closeChat, onClose]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleContactSelect = (contactId: number) => {
    openChat(contactId);
  };

  const handleSendMessage = (message: string) => {
    if (activeContactId) {
      sendMessage(activeContactId, message);
    }
  };

  const handleBackToContacts = () => {
    closeChat();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-50 z-30 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Overlay Container */}
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
        <div
          className={`w-full max-w-md h-full max-h-[600px] bg-white rounded-lg shadow-2xl transform transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={onClose}
              className="p-2 bg-white"
              aria-label="채팅 닫기"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#323B49"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="h-full rounded-lg overflow-hidden">
            {activeContact ? (
              <ChatRoom
                contact={activeContact}
                messages={activeMessages}
                onBack={handleBackToContacts}
                onSendMessage={handleSendMessage}
                product={activeProductData || undefined}
              />
            ) : (
              <ChatContactPanel
                contacts={contacts}
                onContactSelect={handleContactSelect}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}